import React, { forwardRef, memo } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { Icon } from '@modulor/react';
import { ButtonWithAnalytics } from '../../ui-components/Button/Button';
import { LinkWithAnalytics } from '../../Link/Link';
import GameCardLabels from './GameCardLabels/GameCardLabels';
import { useQueryProviderTitle } from '../../../hooks/API/useQueryProviders';
import { useIsSC } from '../../../hooks/useIsSC';
import { useViewConfig } from '../../../hooks/useViewConfig';
import useIsDesktopLayout from '../../../hooks/useIsDesktopLayout';
import { getGameDetailRoute } from '../../../utils/routes';
import { getGameImagePath } from '../../../utils/imagePath';
import { getDataTestAttributes } from '../../../utils/testing-locators';
import {
  gameImageAlternativeSelector,
  gameImageSelector,
} from '../../../redux/reducers/app/configuration';
import { brandSelector } from '../../../redux/reducers/app/params';
import {
  GAME_CARD,
  GAME_IMAGE,
  GAME_LINK,
  GAME_PLAY_BUTTON,
} from '../../../constants/dataTestIds';
import { EVENT_ACTION } from '../../../constants/ga';
import { ANALYTICS_SCHEMA_URLS } from '../../../constants/analytics';
import {
  BONUSES_WAGERING_GAMES,
  ROOT,
  SEARCH,
} from '../../../constants/routeTemplates';
import { SCCOM_BRAND } from '../../../constants/brands';
import { GAME_ITEM_SKELETON_COLLECTION } from '../../../constants/viewConfig';
import GameCardBets from './GameCardBets/GameCardBets';
import { ReactComponent as FallbackIcon } from '../../../AppSCCOM/assets/icons/image-fallback.svg'; // no modulor icon
import Image from '../../Image/Image';
import styles from './GameGridItem.module.css';

export const getGameEventAction = (path) => {
  const isSearchPage = path === SEARCH;
  const isRootPage = path === ROOT;
  const isWageringGames = path === BONUSES_WAGERING_GAMES;

  if (isSearchPage) {
    return EVENT_ACTION.SEARCH_PLAY;
  }

  if (isWageringGames) {
    return EVENT_ACTION.WAGERING_BONUS_PAGE_PLAY;
  }

  if (!isRootPage) {
    return EVENT_ACTION.FILTER_PLAY;
  }

  return EVENT_ACTION.WIDGET_PLAY;
};

const createAnalyticsData = (analyticsData, eventData, game) => ({
  ...(analyticsData || {}),
  game_id: eventData.game_id,
  position_index: eventData.position_index,
  section_index: eventData.section_index,
  schema: ANALYTICS_SCHEMA_URLS.PLAY_GAME_CLICK,
  provider_id: analyticsData?.provider_id || game.provider || '',
});

const getPlayIconProps = (brand) => {
  switch (brand) {
    case SCCOM_BRAND:
      return {
        size: 32,
      };

    default:
      return {
        size: 28,
      };
  }
};

const GameGridItem = memo(
  forwardRef(
    (
      {
        game,
        index,
        isLoading,
        sectionIndex,
        eventAction,
        eventActionEntityFieldName,
        eventActionEntityFieldValue,
        analyticsData,
        className,
        imgClassName,
      },
      ref
    ) => {
      const isDesktop = useIsDesktopLayout();
      const isSCCOM = useIsSC();
      const brand = useSelector(brandSelector);
      const gameImagePath = useSelector(gameImageSelector);
      const imageURL = getGameImagePath(gameImagePath, game?.id, isDesktop);
      const imageAlternativePath = useSelector(gameImageAlternativeSelector);
      const GameItemSkeleton = useViewConfig(GAME_ITEM_SKELETON_COLLECTION);

      // TODO: temporary, until BE provides provider translationKey in game obj
      const providerTitle = useQueryProviderTitle(game?.provider);
      //

      const { path } = useRouteMatch();
      // need to rework full system of event actions depends on parent component and not on page
      const customEventAction = getGameEventAction(path);
      const dataLayerEventData = {
        eventAction: eventAction || customEventAction,
        position_index: index,
        section_index: sectionIndex,
        game_id: game?.id,
        ...(eventActionEntityFieldName
          ? {
              [`${eventActionEntityFieldName}_id`]: eventActionEntityFieldValue,
            }
          : {}),
      };

      const history = useHistory();
      const clickEventHandler = () => {
        history.push(getGameDetailRoute(game?.id));
      };

      return (
        <div
          ref={ref}
          className={classnames(
            styles.root,
            path === SEARCH && styles['lc-search'],
            {
              [styles['root-sccom']]: isSCCOM,
            },
            className
          )}
          {...getDataTestAttributes(GAME_CARD, game?.id)}
        >
          <div
            className={classnames(styles.content, {
              [styles['content--loading']]: isLoading,
              [styles['content-sccom']]: isSCCOM,
            })}
          >
            {isLoading ? (
              <GameItemSkeleton />
            ) : (
              <>
                <GameCardLabels
                  gameId={game?.id}
                  tags={game?.tags}
                  language={game?.dealerLanguage}
                />

                <div className={styles.imgWrapper}>
                  {isSCCOM ? (
                    <Image
                      src={imageURL}
                      alt={game?.translationKey}
                      fallback={
                        <div className={styles.fallback_sc}>
                          <FallbackIcon />
                        </div>
                      }
                      className={classnames(styles.img, styles['img-sccom'])}
                      {...getDataTestAttributes(GAME_IMAGE)}
                    />
                  ) : (
                    <Image
                      alt={game?.translationKey}
                      src={imageURL}
                      srcAlternative={getGameImagePath(
                        imageAlternativePath,
                        game?.id
                      )}
                      fallback={
                        <div className={styles.fallback}>
                          <Icon
                            name="livecasino_filled"
                            className={styles.fallback_icon}
                            size={56}
                            {...getDataTestAttributes(GAME_IMAGE)}
                          />
                        </div>
                      }
                      className={classnames(styles.img, imgClassName)}
                      {...getDataTestAttributes(GAME_IMAGE)}
                    />
                  )}
                  <ButtonWithAnalytics
                    text=""
                    className={styles.mobileButton}
                    intents={['primary', 'md', 'round']}
                    dataLayerEventData={dataLayerEventData}
                    {...getDataTestAttributes(GAME_PLAY_BUTTON)}
                    onClick={clickEventHandler}
                    analyticsData={createAnalyticsData(
                      analyticsData,
                      dataLayerEventData,
                      game
                    )}
                  />
                </div>
                <div
                  className={classnames(styles.caption, {
                    [styles['caption-sccom']]: isSCCOM,
                  })}
                >
                  <span className={styles.provider}>{providerTitle}</span>
                  <LinkWithAnalytics
                    className={styles.title}
                    intents={[]}
                    dataLayerEventData={dataLayerEventData}
                    {...getDataTestAttributes(GAME_LINK)}
                    to={getGameDetailRoute(game?.id)}
                  >
                    {game?.translationKey}
                  </LinkWithAnalytics>
                  <GameCardBets className={styles.bets} gameId={game?.id} />
                </div>

                <div
                  title={game?.translationKey}
                  className={classnames(styles.overlay, {
                    [styles['overlay-sccom']]: isSCCOM,
                  })}
                >
                  <ButtonWithAnalytics
                    text={
                      <Icon
                        name="play_filled"
                        fill="text-primary-control"
                        {...getPlayIconProps(brand)}
                      />
                    }
                    className={styles.playBtn}
                    intents={['primary', 'md', 'round']}
                    dataLayerEventData={dataLayerEventData}
                    {...getDataTestAttributes(GAME_PLAY_BUTTON)}
                    onClick={clickEventHandler}
                    analyticsData={createAnalyticsData(
                      analyticsData,
                      dataLayerEventData,
                      game
                    )}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      );
    }
  )
);

GameGridItem.propTypes = {
  game: PropTypes.shape({
    tags: PropTypes.arrayOf(PropTypes.string),
    id: PropTypes.string,
    provider: PropTypes.string,
    translationKey: PropTypes.string,
    dealerLanguage: PropTypes.string,
  }),
  index: PropTypes.number,
  isLoading: PropTypes.bool,
  categoryId: PropTypes.string,
  className: PropTypes.string,
  imgClassName: PropTypes.string,
};
GameGridItem.defaultProps = {
  isLoading: false,
};

export default GameGridItem;
